import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/store/types';
import filterRepository, { FilterDataResponse } from '@/repositories/filterRepository';
import queryLib from '@/library/queryLib';
import filterService from '@/services/filterService';
import { AvailableFilter, FILTER_DISTRIBUTION } from '@/config/filters';
import { isSchoolType } from '@/types/schools';
import { Entries } from '@/types/helper';

export type FilterKey = keyof FilterState;

type FilterValueUpdate = {
  filterKey: FilterKey;
  value: string;
}
type FilterOptionsUpdate = {
  filterKey: FilterKey;
  options: string[];
}
type FilterDataUpdate = {
  filterKey: FilterKey;
  data: any;
}
type FilterOptionsCountUpdate = {
  filterKey: Exclude<FilterKey, 'naam'>;
  option: FilterOption;
  count: number;
}

export type FilterValues = FilterProto<string | undefined>;

// for the filter options the value is actually the title..
// and the 'real' value is converted to lowercase everywhere.
export type FilterOption = {
  title?: string; // TODO lets use a title field rather than a 'incorrect' value
  value: string;
  count: number;
  isInactive?: (filter: FilterValues) => boolean;
  parent?: string;
}
export interface Filter {
  value?: string;
  options?: FilterOption[];
  data?: any;
}

export type FilterProto<T> = {
  [key in AvailableFilter]: T;
}

export type FilterState = FilterProto<Filter>

// the values are lowercase (the titles can be whatever)
export const DEFAULT_FILTER_VALUES: FilterValues = {
  naam: undefined,
  type: 'alle schoolsoorten',
  schooladvies: 'alle adviezen',
  soortonderwijs: 'breed of categoraal',
  stadsdeel: 'heel amsterdam',
  denominatie: 'alle bekostigde richtingen',
  onderwijsconcept: 'alle onderwijsconcepten',
  inspectieoordeel: 'alle inspectieoordelen',
  ondersteuningsaanbod: 'volledig aanbod',
  ondersteunings_subaanbod: 'volledig subaanbod'
};

function isValidFilterKey(value: string, state: FilterState): value is keyof FilterState {
  return value in state;
}

function isDefaultValue<K extends keyof FilterValues>(
  filterKey: K,
  filterValue: FilterValues[K],
): boolean {
  return DEFAULT_FILTER_VALUES[filterKey] === filterValue;
}

const initialFilterValues = getInitialFilterValues();

export default {
  namespaced: true,
  state: {
    naam: { value: initialFilterValues.naam },
    type: {
      value: initialFilterValues.type,
      options: [],
    },
    schooladvies: {
      value: initialFilterValues.schooladvies,
      options: [],
    },
    soortonderwijs: {
      value: initialFilterValues.soortonderwijs,
      options: [],
    },
    stadsdeel: {
      value: initialFilterValues.stadsdeel,
      options: [],
      data: [],
    },
    denominatie: {
      value: initialFilterValues.denominatie,
      options: [],
    },
    onderwijsconcept: {
      value: initialFilterValues.onderwijsconcept,
      options: [],
    },
    inspectieoordeel: {
      value: initialFilterValues.inspectieoordeel,
      options: [],
    },
	ondersteuningsaanbod: {
		value: initialFilterValues.ondersteuningsaanbod,
		options: [],
	  },
	ondersteunings_subaanbod: {
		value: initialFilterValues.ondersteunings_subaanbod,
		options: [],
	}
  },
  getters: {
    getData(state) {
     return (key: string) => {
      if (!isValidFilterKey(key, state)) return null;

      return state[key]?.data;
     };
    },

    // this is the whole state of the filter store
    filter(state) {
      return state;
    },
    // a simple <key, value> map of the filters
    // which actually is the active filters as an object
    filterValues(state) {
      const result = {} as FilterValues;
      for (const key in state) {
        const filterKey = key as FilterKey;
        if ('value' in state[filterKey]) {
          result[filterKey] = state[filterKey].value;
        }
      }
      return result;
    },
    // a simple <key, options> map of the filters
    filterOptions(state) {
      const result = {} as FilterProto<FilterOption[]>;
      for (const key in state) {
        const filterKey = key as FilterKey;
        if ('options' in state[filterKey]) {
          result[filterKey] = state[filterKey].options || [];
        }
      }
      return result;
    },
    typeValue(state) {
      return state.type.value;
    },
    isFilterActive(state) {
      return (key: AvailableFilter) => {
        const currentSchoolType = (
          !isSchoolType(state.type.value) ? 'all' : state.type.value
        );
        const selectedFilter = FILTER_DISTRIBUTION[currentSchoolType];
        const currentActiveFilters = selectedFilter || FILTER_DISTRIBUTION.default;
        return currentActiveFilters.includes(key);
      };
    },
  },
  // mutations are not meant to be called directly. Rather use the actions that
  // will automatically update other relevant dependencies
  mutations: {
    setFilterValue(state, { filterKey, value }: FilterValueUpdate) {
      Vue.set(state[filterKey], 'value', value);

      // only store filter on the query if it is necessary (thus: isn't the default value)
      queryLib.addQueryParams({ [filterKey]: value });
      if (DEFAULT_FILTER_VALUES[filterKey] === value) {
        queryLib.removeQueryParam(filterKey as string);
      }
    },
    setFilterOptions(state, { filterKey, options }: FilterOptionsUpdate) {
      Vue.set(state[filterKey], 'options', options);
    },
    setFilterData(state, { filterKey, data }: FilterDataUpdate) {
      Vue.set(state[filterKey], 'data', data);
    },
    setFilterOptionCount(state, { filterKey, option, count }: FilterOptionsCountUpdate) {
      const filterOption = state[filterKey].options?.find((o) => o.value === option.value);
      if (filterOption) {
        Vue.set(filterOption, 'count', count);
      }
    },
  },
  actions: {
    async resetAllFilters({ getters, dispatch }) {
      const { filterValues }: { filterValues: FilterValues } = getters;

      const promises = (Object.entries(filterValues) as Entries<FilterValues>)
        // Filter out the filters that already have a default value
        .filter(([filterKey, filterValue]) => !isDefaultValue(filterKey, filterValue))
        // Dispatch the updateFilterToDefault function and add return the promise
        .map(([filterKey]) => dispatch('updateFilterToDefault', filterKey));

      await Promise.all(promises);
    },
    async loadFilterData({ commit, state }: { commit: any; state: FilterState}) {
      const { data }: FilterDataResponse = await filterRepository.listFilterData();
      let key: keyof typeof data;
      for (key in data) {
        if (
          isValidFilterKey(key, state)
          && state[key]
          && Object.prototype.hasOwnProperty.call(state[key], 'data')
        ) {
          commit('setFilterData', { filterKey: key, data: data[key] });
        }
      }

      return data;
    },
    async loadFilterOptions({ commit }) {
      const { data } = await filterRepository.listFilterOptions();
      for (const filterKey in data) {
        commit('setFilterOptions', { filterKey, options: data[filterKey] });
      }
      return data;
    },
    async removeFilterValue({ dispatch }, filterKey: keyof FilterValues) {
      const value = DEFAULT_FILTER_VALUES[filterKey];
      const data = { filterKey, value };
      dispatch('updateFilterValue', data);
    },
    async updateFilterToDefault({ commit }, filterKey: keyof FilterValues) {
      const value = DEFAULT_FILTER_VALUES[filterKey];
      const data = { filterKey, value };
      commit('setFilterValue', data);
    },
    async updateFilterValue({ commit, dispatch }, data: FilterDataUpdate, cleanup = true) {
      commit('setFilterValue', data);
      dispatch('cleanupFilters');
      dispatch('updateFilterOptionsCount');
      dispatch('comparator/evaluateCurrentComparator', null, { root: true });
    },
    async cleanupFilters({ dispatch, getters }) {
      const { filterValues, filterOptions }: {
        filterValues: FilterValues;
        filterOptions: FilterProto<FilterOption[]>; } = getters;

      // Remove invalid filterValues. Certain filterValues are only active when
      // there is a certain subfilter active. This cleanup action will validate
      // all current selected filters and unset any inActive filterOptions
      // Also it will remove filters that are not available in the current type
      for (
        const [filterKey, filterValue] of Object.entries(filterValues) as Entries<FilterValues>
      ) {
        const currentOption = filterOptions[filterKey] ? filterOptions[filterKey]
          .find((o: FilterOption) => o.value === filterValue) : null;

        if (
          // Current option is inactive
          (currentOption && currentOption.isInactive && currentOption.isInactive(filterValues))

          // Or filter is not active and not the default value
          || (!getters.isFilterActive(filterKey) && !isDefaultValue(filterKey, filterValue))
        ) {
          // eslint-disable-next-line no-await-in-loop
          await dispatch('updateFilterToDefault', filterKey);
        }
      }
    },
    // update the count of all filter options - should be called once after
    // loading the filterOptions and after every filter update
    async updateFilterOptionsCount({ commit, getters, rootGetters }) {
      const { filterValues, filterOptions } = getters;
      const scholen = rootGetters['scholen/scholen'];

      for (const filterKey in filterOptions) {
        if (filterKey === 'naam') {
          continue; // naam filter has no options
        }
        for (const filterOption of filterOptions[filterKey]) {
          const filter = { ...filterValues, [filterKey]: filterOption.value };
          const filteredScholen = filterService.filterScholen(scholen, filter);
          commit('setFilterOptionCount', { filterKey, option: filterOption, count: filteredScholen.length });
        }
      }
    },
  },
} as Module<FilterState, RootState>;

// Initial filter values are based on whatever is present in the query for the
// filterKeys, or has a hardcoded fall-back to its default value
function getInitialFilterValues(): FilterValues {
  const rawQueryParams: FilterProto<string> = queryLib.getQueryParams();

  // Legacy links weren't all lowercase, therefore transform using this function
  const queryParams: Partial<FilterProto<string | undefined>> = {};
  Object.entries(rawQueryParams).forEach(([key, value]) => {
    queryParams[(key as keyof FilterValues)] = value ? value.toLowerCase() : undefined;
  });

  const filterValues = {
    naam: queryParams.naam || DEFAULT_FILTER_VALUES.naam,
    type: queryParams.type || DEFAULT_FILTER_VALUES.type,
    schooladvies: queryParams.schooladvies || DEFAULT_FILTER_VALUES.schooladvies,
    soortonderwijs: queryParams.soortonderwijs || DEFAULT_FILTER_VALUES.soortonderwijs,
    stadsdeel: queryParams.stadsdeel || DEFAULT_FILTER_VALUES.stadsdeel,
    denominatie: queryParams.denominatie || DEFAULT_FILTER_VALUES.denominatie,
    onderwijsconcept: queryParams.onderwijsconcept || DEFAULT_FILTER_VALUES.onderwijsconcept,
    inspectieoordeel: queryParams.inspectieoordeel || DEFAULT_FILTER_VALUES.inspectieoordeel,
    ondersteuningsaanbod: queryParams.ondersteuningsaanbod || DEFAULT_FILTER_VALUES.ondersteuningsaanbod,
	ondersteunings_subaanbod: queryParams.ondersteunings_subaanbod || DEFAULT_FILTER_VALUES.ondersteunings_subaanbod
  };

  return filterValues;
}
